/**
 * Supabase-backed partial verse highlights (character offsets in verse body text).
 * Table: verse_text_highlights. Multiple rows per verse allowed.
 */

/** @typedef {'yellow'|'blue'|'pink'|'green'} HighlightColorId */

export const HIGHLIGHT_COLOR_IDS = /** @type {const} */ ([
  "yellow",
  "blue",
  "pink",
  "green",
]);

export const HIGHLIGHT_COLORS = [
  { id: "yellow", label: "Yellow", className: "bg-yellow-200/90 dark:bg-yellow-500/25", hex: "#fef08a" },
  { id: "blue", label: "Blue", className: "bg-blue-200/90 dark:bg-blue-500/25", hex: "#bfdbfe" },
  { id: "pink", label: "Pink", className: "bg-pink-200/90 dark:bg-pink-500/25", hex: "#fbcfe8" },
  { id: "green", label: "Green", className: "bg-green-200/90 dark:bg-green-500/25", hex: "#bbf7d0" },
];

export function highlightHexForColorId(colorId) {
  const row = HIGHLIGHT_COLORS.find((c) => c.id === colorId);
  return row?.hex ?? "transparent";
}

/**
 * @returns {Promise<Record<number, { id: string, start_offset: number, end_offset: number, color: string }[]>>}
 */
export async function fetchChapterTextHighlights(supabase, { book, chapter }) {
  const { data, error } = await supabase
    .from("verse_text_highlights")
    .select("id, verse, start_offset, end_offset, color")
    .eq("book", book)
    .eq("chapter", chapter);

  if (error) {
    console.error("fetchChapterTextHighlights", error);
    throw error;
  }

  /** @type {Record<number, { id: string, start_offset: number, end_offset: number, color: string }[]>} */
  const map = {};
  (data || []).forEach((row) => {
    if (!map[row.verse]) map[row.verse] = [];
    map[row.verse].push({
      id: row.id,
      start_offset: row.start_offset,
      end_offset: row.end_offset,
      color: row.color,
    });
  });
  Object.keys(map).forEach((k) => {
    map[k].sort((a, b) => a.start_offset - b.start_offset || a.end_offset - b.end_offset);
  });
  return map;
}

/**
 * @returns {Promise<{ id: string, verse: number, start_offset: number, end_offset: number, color: string }>}
 */
export async function insertTextHighlight(supabase, userId, payload) {
  const row = {
    user_id: userId,
    book: payload.book,
    chapter: payload.chapter,
    verse: payload.verse,
    start_offset: payload.start_offset,
    end_offset: payload.end_offset,
    color: payload.color,
  };

  const { data, error } = await supabase
    .from("verse_text_highlights")
    .insert(row)
    .select("id, verse, start_offset, end_offset, color")
    .single();

  if (error) {
    console.error("insertTextHighlight", error);
    throw error;
  }

  return data;
}

export async function deleteTextHighlightById(supabase, id) {
  const { error } = await supabase.from("verse_text_highlights").delete().eq("id", id);
  if (error) {
    console.error("deleteTextHighlightById", error);
    throw error;
  }
}

/** Delete all saved highlights that overlap [selStart, selEnd) in verse body (half-open intervals). */
export async function deleteOverlappingTextHighlights(
  supabase,
  { book, chapter, verse, selStart, selEnd }
) {
  const { data: rows, error } = await supabase
    .from("verse_text_highlights")
    .select("id, start_offset, end_offset")
    .eq("book", book)
    .eq("chapter", chapter)
    .eq("verse", verse);

  if (error) {
    console.error("deleteOverlappingTextHighlights", error);
    throw error;
  }

  const overlapping = (rows || []).filter(
    (r) => r.start_offset < selEnd && r.end_offset > selStart
  );
  if (!overlapping.length) return;

  const ids = overlapping.map((r) => r.id);
  const { error: delErr } = await supabase.from("verse_text_highlights").delete().in("id", ids);
  if (delErr) {
    console.error("deleteOverlappingTextHighlights delete", delErr);
    throw delErr;
  }
}
